import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { ApolloClient, ErrorLike } from '@apollo/client';
import { skipToken } from '@apollo/client/react';
import type {
  SellerJourneyRouteQuery,
  SellerJourneyRouteQueryVariables,
} from '__generated__/zeus/graphql';
import { SellerJourneyRouteDocument } from '__generated__/zeus/graphql';
import { useQuery } from 'platform/apollo';
import { DevError } from 'platform/Error';
import { useParams } from 'react-router';
import { useZeusApolloClient } from 'setupClient';

export type SellerJourneyRoute = NonNullable<
  SellerJourneyRouteQuery['customerAccount']['seller']['transaction']
>;

type SellerJourneyRouteContextValue = {
  transaction: SellerJourneyRoute | null | undefined;
  loading: boolean;
  error: ErrorLike | undefined;
  refetch: (
    variables?: Partial<SellerJourneyRouteQueryVariables>,
  ) => Promise<ApolloClient.QueryResult<SellerJourneyRouteQuery>>;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
};

const SellerJourneyRouteContext = createContext<SellerJourneyRouteContextValue | null>(null);

export const SellerJourneyRouteProvider: FC<PropsWithChildren> = ({ children }) => {
  const { addressUUID } = useParams();
  const client = useZeusApolloClient();
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    SellerJourneyRouteDocument,
    !addressUUID
      ? skipToken
      : {
          client,
          variables: { addressId: addressUUID },
          fetchPolicy: 'network-only',
        },
  );
  const transaction = data?.customerAccount.seller.transaction;

  const value = useMemo<SellerJourneyRouteContextValue>(
    () => ({
      transaction,
      loading,
      error,
      refetch,
      startPolling,
      stopPolling,
    }),
    [error, loading, refetch, startPolling, stopPolling, transaction],
  );

  return (
    <SellerJourneyRouteContext.Provider value={value}>
      {children}
    </SellerJourneyRouteContext.Provider>
  );
};

export const useSellerJourneyRoute = (): SellerJourneyRouteContextValue => {
  const value = useContext(SellerJourneyRouteContext);
  if (!value) {
    throw new DevError('useSellerJourneyRoute must be used inside SellerJourneyRouteProvider');
  }
  return value;
};
